import { inject, Injectable } from '@angular/core';
import { AuthData } from '@app/core/models/auth.model';
import { Events } from '@app/core/models/event-map';
import { PublishSubscribeService } from '@app/core/services/publish-subscribe.service';
import { hasAccessTokenExpired } from '@app/core/utils/token.util';
import { UserContext } from '@app/shared/models/access-control/user-context.model';
import { PortalUser } from '@app/shared/models/portal-user.model';
import { environment } from '@environments/environment';
import { catchError, forkJoin, map, Observable, of, switchMap, tap, throwError } from 'rxjs';
import { ConstantsService } from '../internal-services/constants-internal.service';
import { PermissionService } from './permission.service';
import { PortalUserService } from './portal-user.service';

@Injectable({
  providedIn: 'root'
})
export class UserContextService {

  #permissionService = inject(PermissionService);
  #portalUserService = inject(PortalUserService);
  #publishSubscribeService = inject(PublishSubscribeService)

  private userContext: UserContext | undefined;
  private isInitialized: boolean = false

  init(authData: AuthData): Observable<UserContext> {
    return this.#permissionService.init(authData).pipe(
      switchMap(() => forkJoin([
        this.#portalUserService.init(authData.userContext)
      ])),
      switchMap(() => {
        // Create user context.
        const userContext = this.mapUserContext(authData)
        this.userContext = userContext

        // Run and set additional data when both permissionService.init() and
        // portalUser.init() is ready.
        return this.loadAdditionalData().pipe(map(() => userContext))
      }),
      catchError((err) => {
        console.error('UserContext init failed', err)
        return throwError(() => new Error('UserContext initialization failed'))
      })
    )
  }

  /**
   * Initializes a minimal UserContext object from the provided auth token data.
   * This method processes and sets permission information via the PermissionService
   * and maps the rest of the user metadata into a UserContext instance.
   *
   * NOTE! The `portalUserState` field will *not* be fully initialized by this method.
   * Any state selector or consumer depending on `userContext.portalUserState` will receive `undefined`.
   * This method is intended to be removed once the full `init()` method is adopted everywhere.
   *
   * @param authData
   * @returns {Observable<UserContext>} UserContext object
   */
  initLight(authData: AuthData): Observable<UserContext> {
    return this.#permissionService.init(authData).pipe(
      map(() => {
        this.userContext = this.mapUserContext(authData)
        return this.userContext
      }),
      catchError((err) => {
        console.error('UserContext initLight failed', err)
        return throwError(() => new Error('UserContext initialization failed'))
      })
    )
  }

  private mapUserContext(authData: AuthData): UserContext {
    const baseRole: string = authData.userContext.BaseRole
    const userRole: number = authData.userContext.UserRole

    return {
      selectedUserId: undefined,
      selectedDomainId: undefined,
      selectedUser: undefined,
      username: authData.username,
      userFullname: authData.userContext.Fullname,
      sessionId: authData.sessionId,
      salesCompany: authData.salesCompany,
      prodCompany: authData.prodCompany,
      permission: this.#permissionService.permissions,
      portalUserState: this.#portalUserService.state,
      adDomain: environment.activeDirectoryDomain,
      axUserId: authData.userContext.AxUserId,
      baseRole: baseRole,
      userRole: userRole,
      resellerType: authData.userContext.ResellerType,
      company: authData.userContext.Company,
      svfAccount: authData.userContext.SvfAccount,
      networkAlias: authData.userContext.NetworkAlias,
      networkDomain: authData.userContext.NetworkDomain,
      preSelectedBrand: authData.userContext.PreSelectedBrand,
      portalCompanies: [authData.salesCompany, authData.prodCompany],
      isAuthenticated: !hasAccessTokenExpired(authData),
      isAdministrator: baseRole === ConstantsService.CustportalRole.ENUM_ADMIN,
      isAftermarket: authData.userContext.IsAftermarket,
      isAftermarketAdmin: this.hasAftermarketRole('ADMIN'),
      isAftermarketManager: this.hasAftermarketRole('MANAGER'),
      isAftermarketReseller: this.hasAftermarketRole('RESELLER'),
      isAftermarketTechnician: this.hasAftermarketRole('TECHNICIAN'),
      isAssembler: authData.userContext.IsAssembler,
      isCrossCompany: authData.salesCompany !== authData.prodCompany,
      isCustomerAdministrator: userRole === ConstantsService.AccessLevel.ENUM_CUST_ADMIN,
      isReseller: authData.userContext.IsReseller,
      isRC_MC: baseRole === ConstantsService.CustportalRole.ENUM_RCMC,
      allowFieldChanges: authData.userContext.AllowFieldChanges,
      allowDisplacements: authData.userContext.AllowDisplacements,
      showPricesValue: authData.userContext.ShowPricesValue,
      showVat: false,
      showUValue: true,
      hasBrandSP: authData.userContext.HasBrandSp,
      hasBrandTR: authData.userContext.HasBrandTr,
      hasBrandCC: authData.userContext.HasBrandCc,
      custAccountNumSP: authData.userContext.AccountNumSp,
      custAccountNumTR: authData.userContext.AccountNumTr,
      custAccountNumCC: authData.userContext.AccountNumCc,
      tokenUser: [authData.username, authData.userContext.NetworkDomain, authData.sessionId],
      showMeasurements: false,
      enableConversion: authData.userContext.EnableConversion
    }
  }

  private loadAdditionalData(): Observable<void> {
    const adUserId = this.userContext.username
    const adDomain = this.userContext.adDomain

    this.setSelectedUser(adUserId, adDomain) // Current logged in user.

    return this.#portalUserService.loadUsers(adUserId, adDomain).pipe(
      switchMap(() => {

        if (this.userContext.isAftermarket) {
          this.ready()
          return of(void 0)
        }

        return this.#portalUserService.loadNewWebQuotationStartsAt()
      }),
      tap(() => this.ready()),
      map(() => void 0),
      catchError((err) => {
        console.error('Failed to load additional user data', err?.message);
        return throwError(() => new Error('Failed to load additional user data'));
      })
    )
  }

  private ready(): void {
    this.isInitialized = true
  }

  setSelectedPortalUser(portalUser: PortalUser): void {
    this.userContext.selectedUser = portalUser
    this.setSelectedUser(portalUser.NETWORKALIAS, portalUser.NETWORKDOMAIN)
    this.#publishSubscribeService.trigger(Events.UserService.SELECTED_USER_CHANGED)
  }

  setSelectedUser(adUserId: string, adDomainId: string) {
    this.userContext.selectedUserId = adUserId
    this.userContext.selectedDomainId = adDomainId
  }

  private hasAftermarketRole(...roles: string[]): boolean {
    const permission = this.#permissionService.getAreaPermission('Aftermarket', 'aftermarket_role').value
    return roles.includes(permission);
  }
}
